import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import config from "../../config";
import { Row, Col, Button, Skeleton, DatePicker, Form, Result } from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined, InboxOutlined } from "@ant-design/icons";
import StatBox from "./StatBox";
import moment from "moment";
import LoginChecked from "../Shared/LoginChecked";

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "0";
  const n = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString("en-PK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function TradingAccounts() {
  const { RangePicker } = DatePicker;

  const [loading, setLoading] = useState(false);
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const [StartDate, SetStartDate] = useState(null);
  const [EndDate, SetEndDate] = useState(null);
  const [listTradingAccount, setListTradingAccount] = useState({});
  const [tradingData, setTradingData] = useState({});

  useEffect(() => {
    document.title = "Trading Account";
    if (!StartDate || !EndDate) {
      setTradingData({});
      setListTradingAccount({});
      return;
    }

    setLoading(true);
    const data = {
      UserID: UserID,
      AccessKey: AccessKey,
      StartDate: StartDate,
      EndDate: EndDate,
    };

    const api_config = {
      method: "post",
      url: config.base_url + "Report/GetTradingAccountReport",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(api_config)
      .then(function (response) {
        if (response.data.status_code == 1) {
          setTradingData(response.data);
          setListTradingAccount(response.data.Trading || {});
        }
        setLoading(false);
      })
      .catch(function (error) {
        console.log(error);
        setLoading(false);
      });
  }, [StartDate, EndDate, UserID, AccessKey]);

  const handleDateChange = useCallback((_date, value) => {
    if (!value || !value[0] || !value[1]) {
      SetStartDate(null);
      SetEndDate(null);
      return;
    }
    SetStartDate(value[0]);
    SetEndDate(value[1]);
  }, []);

  const disableDate = (current) => {
    return current && current > moment().endOf("day");
  };

  const printHref =
    StartDate && EndDate
      ? `/report-trading/print-list/?StartDate=${encodeURIComponent(
          StartDate
        )}&EndDate=${encodeURIComponent(EndDate)}`
      : "#";

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Trading Account</h3>

            <div className="header-actions">
              <NavLink
                to={printHref}
                target="_blank"
                style={{
                  pointerEvents: StartDate && EndDate ? "auto" : "none",
                  opacity: StartDate && EndDate ? 1 : 0.5,
                }}
              >
                <Button
                  type="dashed"
                  size="large"
                  shape="round"
                  disabled={!StartDate || !EndDate}
                >
                  <PrinterOutlined />
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>

          <p className="text-muted" style={{ marginBottom: 16, maxWidth: 720 }}>
            Figures use sale and purchase line amounts (selling price × quantity
            and purchase rate × quantity). Opening and closing stock are valued
            at cost using product balances and last purchase rate (same basis as
            other stock reports). Tax and sale/purchase returns are not applied.
          </p>

          <div className="filter-wrap">
            <Form>
              <Form.Item label="Period">
                <RangePicker
                  allowClear
                  onChange={handleDateChange}
                  disabledDate={disableDate}
                />
              </Form.Item>
            </Form>
          </div>

          {loading ? (
            <>
              <Row>
                <Col xs={24} md={16}>
                  <Skeleton active />
                  <Skeleton active />
                </Col>
              </Row>
            </>
          ) : StartDate != null && EndDate != null ? (
            <>
              <Row gutter={[20, 20]}>
                <Col xs={24} md={12}>
                  <StatBox
                    label="Opening stock (at cost)"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      listTradingAccount.OpeningStock != null
                        ? listTradingAccount.OpeningStock
                        : 0
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <StatBox
                    label="Closing stock (at cost)"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      listTradingAccount.ClosingStock != null
                        ? listTradingAccount.ClosingStock
                        : 0
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <StatBox
                    label="Purchases (period, at cost)"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      listTradingAccount.TotalPurchase != null
                        ? listTradingAccount.TotalPurchase
                        : 0
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <StatBox
                    label="Sales (period, revenue)"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      listTradingAccount.TotalSales != null
                        ? listTradingAccount.TotalSales
                        : 0
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <StatBox
                    label="Cost of goods sold"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      listTradingAccount.CostOfGoodsSold != null
                        ? listTradingAccount.CostOfGoodsSold
                        : 0
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <StatBox
                    label="Gross profit"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      tradingData.GrossProfit != null
                        ? tradingData.GrossProfit
                        : 0
                    )}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <StatBox
                    label="Loss (if any)"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      listTradingAccount.LossSuffered != null
                        ? listTradingAccount.LossSuffered
                        : 0
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <StatBox
                    label="Debit total (trading account)"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      tradingData.TotalAmountLeft != null
                        ? tradingData.TotalAmountLeft
                        : 0
                    )}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <StatBox
                    label="Credit total (trading account)"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0px",
                    }}
                    value={formatCurrency(
                      tradingData.TotalAmountRight != null
                        ? tradingData.TotalAmountRight
                        : 0
                    )}
                  />
                </Col>
              </Row>
            </>
          ) : (
            <Result
              icon={<InboxOutlined style={{ color: "gray" }} />}
              title={
                <span style={{ color: "gray", fontSize: "16px" }}>
                  Select a date range to load the trading account
                </span>
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

export default TradingAccounts;
